import { TypeIdSchema } from '@unipile/db';
import { Type, type Static } from '@sinclair/typebox';
import { IsoDateTimeSchema } from '../../shared/utils/date-time.schema';

export const PostScopedApiKeyBodySchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 255, description: 'An internal name used to identify the API Key.' }),
    expires_at: IsoDateTimeSchema({ description: 'The date and time when the API Key expires.' }),
  },
  { additionalProperties: false },
);

export const PostApiKeyBodySchema = Type.Composite(
  [
    PostScopedApiKeyBodySchema,
    Type.Object({
      role: Type.Union([Type.Literal('service'), Type.Literal('account')], {
        description: 'The API Key role. Service keys manage the Application; Account keys access connected Accounts.',
      }),
      account_scope_id: Type.Optional(
        TypeIdSchema('accountScope', { description: 'The Scope that restricts an Account API Key.' }),
      ),
    }),
  ],
  { additionalProperties: false },
);

export const PostDelegatedApiKeyBodySchema = Type.Union([
  Type.Composite(
    [
      PostScopedApiKeyBodySchema,
      Type.Object({
        access: Type.Object(
          {
            type: Type.Literal('global', {
              description: 'Grants access to every Account in the Application without management permissions.',
            }),
          },
          {
            additionalProperties: false,
            description: 'The Account access boundary of the API Key.',
          },
        ),
      }),
    ],
    { additionalProperties: false, title: 'Global Account API Key' },
  ),
  Type.Composite(
    [
      PostScopedApiKeyBodySchema,
      Type.Object({
        access: Type.Object(
          {
            type: Type.Literal('scope', {
              description: 'Restricts access to Accounts permanently assigned to one Scope.',
            }),
            scope_id: TypeIdSchema('accountScope', {
              minLength: 32,
              maxLength: 32,
              description: 'The ID of the active Scope that the API Key can access.',
            }),
          },
          {
            additionalProperties: false,
            description: 'The Account access boundary of the API Key.',
          },
        ),
      }),
    ],
    { additionalProperties: false, title: 'Scoped Account API Key' },
  ),
]);

export type PostApiKeyBody = Static<typeof PostApiKeyBodySchema>;
export type PostScopedApiKeyBody = Static<typeof PostScopedApiKeyBodySchema>;
export type PostDelegatedApiKeyBody = Static<typeof PostDelegatedApiKeyBodySchema>;

export const ListDelegatedApiKeysQuerySchema = Type.Object({
  access: Type.Optional(
    Type.Union([Type.Literal('global'), Type.Literal('scope')], {
      description: 'Filter Account API Keys by global or Scope access.',
    }),
  ),
  account_scope_id: Type.Optional(TypeIdSchema('accountScope', { description: 'Filter API Keys assigned to this Scope.' })),
  offset: Type.Optional(Type.String({ description: 'The number of API Keys to skip for pagination.' })),
  limit: Type.Optional(Type.String({ description: 'The maximum number of API Keys to return.', default: '20' })),
});

export type ListDelegatedApiKeysQuery = Static<typeof ListDelegatedApiKeysQuerySchema>;

export const ListApiKeysQuerySchema = Type.Object({
  account_scope_id: Type.Optional(TypeIdSchema('accountScope')),
  role: Type.Optional(
    Type.Union([Type.Literal('service'), Type.Literal('account'), Type.Literal('scoped'), Type.Literal('delegated')]),
  ),
  offset: Type.Optional(Type.String()),
  limit: Type.Optional(Type.String()),
});

export type ListApiKeysQuery = Static<typeof ListApiKeysQuerySchema>;
